'use client';

import { useMemoizedFn } from 'ahooks';
import { useMemo, useRef } from 'react';
import { useStore, useStoreApi } from '@/app/providers/store';
import type { UpdateCreationInteractionStatusFn } from '@/app/types';
import { usePathname, useRouter } from '@/i18n/routing';
import { buildCreationDetailThumbListFromFiles } from '../_utils/creation-detail';
import type { FileItem } from '../types';

interface UseOpenFileParams {
  files: FileItem[];
  initialHasMore: boolean;
  initialPageToken?: string;
  handleVote?: UpdateCreationInteractionStatusFn;
  handleUnVote?: UpdateCreationInteractionStatusFn;
  handleCollect?: UpdateCreationInteractionStatusFn;
  handleUnCollect?: UpdateCreationInteractionStatusFn;
  fetchDetailPage: (pageToken?: string) => Promise<{
    files: FileItem[];
    hasMore: boolean;
    nextPageToken?: string;
  }>;
}

/**
 * 打开 creation 文件：通过路由跳转到 /creation-detail/[creationId]（拦截路由弹窗或整页）
 */
export function useOpenFile(params: UseOpenFileParams) {
  const {
    files,
    initialHasMore,
    initialPageToken,
    fetchDetailPage,
    handleVote,
    handleUnVote,
    handleCollect,
    handleUnCollect,
  } = params;
  const router = useRouter();
  const pathname = usePathname();
  const syncCreationDetailConfig = useStore((s) => s.syncCreationDetailConfig);
  const storeApi = useStoreApi();
  const detailHasMoreRef = useRef(false);
  const detailPageTokenRef = useRef<string | undefined>(undefined);
  const detailLoadingRef = useRef(false);

  const listForDetailConfig = useMemo(() => buildCreationDetailThumbListFromFiles(files), [files]);

  const openCreationDetailByRoute = useMemoizedFn((id: string) => {
    const targetPath = `/creation-detail/${id}`;
    if (pathname.startsWith('/creation-detail/')) {
      router.replace(targetPath);
      return;
    }
    router.push(targetPath);
  });

  const handleDetailLoadMore = useMemoizedFn(async () => {
    if (detailLoadingRef.current || !detailHasMoreRef.current || !detailPageTokenRef.current) return;
    detailLoadingRef.current = true;
    const { files: nextFiles, hasMore, nextPageToken } = await fetchDetailPage(detailPageTokenRef.current);
    detailLoadingRef.current = false;

    detailHasMoreRef.current = hasMore;
    detailPageTokenRef.current = nextPageToken;

    if (nextFiles.length === 0) return;

    const nextList = buildCreationDetailThumbListFromFiles(nextFiles);
    const currentConfig = storeApi.getState().creationDetailConfig?.thumbScrollListConfig;
    const currentList = currentConfig?.list ?? [];
    const merged = [...currentList];
    const getListItemId = (item: (typeof currentList)[number]) => ('id' in item ? item.id : '');
    const existingIds = new Set(currentList.map(getListItemId).filter(Boolean));
    nextList.forEach((item) => {
      const id = 'id' in item ? item.id : '';
      if (!id || existingIds.has(id)) return;
      existingIds.add(id);
      merged.push(item);
    });

    syncCreationDetailConfig({
      openInNewTab: true,
      thumbScrollListConfig: {
        list: merged,
        hasMore: detailHasMoreRef.current,
        onLoadMore: handleDetailLoadMore,
        onThumbnailClick: openCreationDetailByRoute,
      },
      handleVote,
      handleUnVote,
      handleCollect,
      handleUnCollect,
      onClose: () => router.back(),
    });
  });

  const openFileById = useMemoizedFn((creationId: string) => {
    detailHasMoreRef.current = initialHasMore;
    detailPageTokenRef.current = initialPageToken;
    syncCreationDetailConfig({
      openInNewTab: true,
      thumbScrollListConfig: {
        list: listForDetailConfig,
        hasMore: detailHasMoreRef.current,
        onLoadMore: handleDetailLoadMore,
        onThumbnailClick: openCreationDetailByRoute,
      },
      handleVote,
      handleUnVote,
      handleCollect,
      handleUnCollect,
      onClose: () => router.back(),
    });
    openCreationDetailByRoute(creationId);
  });

  return {
    isLoadingDetail: false,
    openFileById,
    isOpenedFile: false,
  };
}
